<script setup>
import { gsap, ScrollTrigger } from '~/vendors/gsap'
import { useLiveEventStore } from '~~/store/event'
import { useIsMobile } from '~~/composables/useIsMobile'

const props = defineProps({
  noClip: {
    type: Boolean,
    default: false,
  },
})

const store = useLiveEventStore()
const isMobile = useIsMobile()
const liveEvent = computed(() => store.liveEvent)
const hasNotification = computed(() => !!liveEvent.value)

// await store.fetchLiveEvent()

const BAR_MINIMUM_WIDTH = 1200

let st
let st2
const resizeObserver = ref(null)
const scrollProgress = ref(0)
const useFloatingBar = ref(null)

// Handlers
function handleResize() {
  useFloatingBar.value = window.innerWidth >= BAR_MINIMUM_WIDTH
}

// Lifecycle
onMounted(() => {
  handleResize()
  // Create an Observer instance
  resizeObserver.value = new ResizeObserver(function () {
    handleResize()
  })
  // start observing a DOM node
  resizeObserver.value.observe(document.documentElement)
})

onBeforeUnmount(() => {
  resizeObserver.value.unobserve(document.documentElement)
})

// ScrollTrigger
const el = ref(null)
watchEffect(() => {
  if (el.value) {
    const clipAngle = isMobile.value ? '60px' : '200px'
    const enterTween =
      !props.noClip &&
      gsap.fromTo(
        el.value.querySelector('.floating-bar__container'),
        {
          clipPath: `polygon(
              0% calc(100% + ${clipAngle}),
              100% calc(100% - 0px),
              100% 100%,
              0% 100%
            )`,
        },
        {
          clipPath: `polygon(
              0% calc(0% + 0px),
              100% calc(0% - ${clipAngle}),
              100% 100%,
              0% 100%
            )`,
          duration: 1,
          ease: 'linear',
          clearProps: 'clipPath',
          paused: true,
        }
      )

    function onUpdate({ progress, start, end }) {
      const windowHeight = window.innerHeight
      const sectionHeight = end - start
      const pixelProgress = sectionHeight * progress
      const tweenProgress = Math.max(
        Math.min(pixelProgress / windowHeight, 1),
        0
      )

      if (props.noClip) {
        if (!el.value) return
        if (tweenProgress > 0.5) {
          el.value.style.opacity = 1
        } else {
          el.value.style.opacity = 0
        }
      } else {
        enterTween.seek(tweenProgress)
      }
    }

    st?.kill()
    st = ScrollTrigger.create({
      trigger: el.value,
      start: 'top bottom',
      end: `bottom bottom`,
      pin: el.value.querySelector('.floating-bar__pin'),
      pinSpacing: false,
      onUpdate,
      onResize: onUpdate,
    })

    st2?.kill()
    st2 = ScrollTrigger.create({
      trigger: el.value,
      start: 'top bottom',
      endTrigger: '#super-footer',
      end: 'top bottom',
      onUpdate({ progress }) {
        scrollProgress.value = progress
      },
    })
  }
})

onBeforeUnmount(() => {
  st?.kill()
})
</script>

<template>
  <aside
    v-if="useFloatingBar"
    ref="el"
    class="floating-bar"
    :class="{ 'floating-bar--no-clip': noClip }"
  >
    <div class="floating-bar__pin">
      <div class="floating-bar__container">
        <TickPagination class="floating-bar__pagination" />

        <div v-if="hasNotification" class="floating-bar__live-notification">
          <p>
            <b v-if="liveEvent.isLivestream">{{ $t('floatingBar.live') }}</b
            ><b v-else>{{ $t('floatingBar.watch') }}</b> {{ liveEvent.title }}
          </p>
          <div class="floating-bar__pulse-container">
            <PulseBeat class="floating-bar__pulse" />
          </div>
        </div>

        <ScrollDistance v-else :progress="scrollProgress" />
      </div>
    </div>
  </aside>
</template>

<style lang="scss">
@use 'sass:math';

// Utils
@import '~/styles/utils/variables';

.floating-bar {
  position: relative;
  z-index: 20;
  pointer-events: none;
  overflow: hidden;
  height: 100%;

  transition: opacity 0.5s;

  &--no-clip {
    opacity: 0;
  }

  &__pin {
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  &__container {
    padding-left: var(--grid-edge);
    padding-bottom: 44px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    position: relative;
    top: -100vh;

    // background: $brand-magenta;
  }

  &__pagination {
    margin: auto 0;
  }

  &__live-notification {
    font-size: 11px;
    font-weight: $font-weight-4G;
    line-height: math.div(12, 11);
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: rgba($brand-white, 0.7);
    display: flex;
    align-items: center;
    position: relative;
    transform: rotate(-90deg);
    transform-origin: top left;
  }

  &__live-notification::before {
    content: '';
    background-color: $brand-white;
    border-radius: 50%;
    display: block;
    width: 5px;
    height: 5px;

    box-shadow: 0px 0px 9px #c80afb;
  }

  &__pulse-container {
    width: 220px;
    height: 220px;
    position: absolute;
    top: 3px;
    left: 1px;
    transform: translate(-50%, -50%);
    z-index: -1;
  }

  &__live-notification b {
    color: $brand-white;
    margin-left: 10px;
    margin-right: 11px;
  }
}
</style>
